import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Toaster } from "react-hot-toast";

// Import shared components
import { AuthProvider } from "./shared/contexts/AuthContext";
import ProtectedRoute from "./shared/components/ProtectedRoute";
import LoginPage from "./shared/components/LoginPage";

import AdminConsole from "./components/AdminConsole";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create modern professional theme
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#2563eb", // Modern blue
      light: "#3b82f6",
      dark: "#1d4ed8",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#8b5cf6", // Modern purple
      light: "#a78bfa",
      dark: "#7c3aed",
      contrastText: "#ffffff",
    },
    success: {
      main: "#10b981", // Modern green
      light: "#34d399",
      dark: "#059669",
    },
    warning: {
      main: "#f59e0b", // Modern amber
      light: "#fbbf24",
      dark: "#d97706",
    },
    error: {
      main: "#ef4444", // Modern red
      light: "#f87171",
      dark: "#dc2626",
    },
    info: {
      main: "#06b6d4", // Modern cyan
      light: "#22d3ee",
      dark: "#0891b2",
    },
    background: {
      default: "#f8fafc", // Very light gray
      paper: "#ffffff",
    },
    text: {
      primary: "#1e293b", // Dark slate
      secondary: "#64748b", // Medium slate
      disabled: "#94a3b8", // Light slate
    },
    divider: "#e2e8f0", // Light border
    grey: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
  },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: "2.025rem", // Reduced by 10% (2.25 * 0.9 = 2.025rem)
      lineHeight: 1.2,
      letterSpacing: "-0.025em",
    },
    h2: {
      fontWeight: 600,
      fontSize: "1.62rem", // Reduced by 10% (1.8 * 0.9 = 1.62rem)
      lineHeight: 1.3,
      letterSpacing: "-0.025em",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.485rem", // Reduced by 10% (1.65 * 0.9 = 1.485rem)
      lineHeight: 1.3,
      letterSpacing: "-0.02em",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.35rem", // Reduced by 10% (1.5 * 0.9 = 1.35rem)
      lineHeight: 1.4,
      letterSpacing: "-0.02em",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.215rem", // Reduced by 10% (1.35 * 0.9 = 1.215rem)
      lineHeight: 1.4,
      letterSpacing: "-0.01em",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.08rem", // Reduced by 10% (1.2 * 0.9 = 1.08rem)
      lineHeight: 1.4,
      letterSpacing: "-0.01em",
    },
    body1: {
      fontSize: "0.945rem", // Reduced by 10% (1.05 * 0.9 = 0.945rem)
      lineHeight: 1.5,
      letterSpacing: "0em",
    },
    body2: {
      fontSize: "0.8775rem", // Reduced by 10% (0.975 * 0.9 = 0.8775rem)
      lineHeight: 1.4,
      letterSpacing: "0em",
    },
    button: {
      fontWeight: 500,
      fontSize: "0.8775rem", // Reduced by 10% (0.975 * 0.9 = 0.8775rem)
      textTransform: "none",
      letterSpacing: "0em",
    },
    caption: {
      fontSize: "0.7425rem", // Reduced by 10% (0.825 * 0.9 = 0.7425rem)
      lineHeight: 1.3,
      letterSpacing: "0.025em",
    },
  },
  shape: {
    borderRadius: 8, // More rounded corners
  },
  shadows: [
    "none",
    "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
    "0 25px 50px -12px rgb(0 0 0 / 0.25)",
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6, // Reduced from 8
          textTransform: "none",
          fontWeight: 500,
          padding: "6px 16px", // More compact padding
          minHeight: "32px", // Consistent height
          boxShadow: "none",
          "&:hover": {
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          },
        },
        contained: {
          "&:hover": {
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          },
        },
        small: {
          padding: "4px 12px",
          minHeight: "28px",
          fontSize: "0.81rem", // Reduced by 10% (0.9 * 0.9 = 0.81rem)
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8, // Reduced from 12
          border: "1px solid #e2e8f0",
          boxShadow: "none",
          "&:hover": {
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "16px", // Reduced from default 24px
          "&:last-child": {
            paddingBottom: "16px", // Reduced from default 24px
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8, // Reduced from 12
          border: "1px solid #e2e8f0",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 6, // Reduced from 8
            "& fieldset": {
              borderColor: "#e2e8f0",
            },
            "&:hover fieldset": {
              borderColor: "#cbd5e1",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#2563eb",
              borderWidth: 2,
            },
          },
          "& .MuiInputBase-input": {
            padding: "10px 12px", // More compact padding
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4, // Reduced from 6
          fontWeight: 500,
          height: "24px", // More compact height
          fontSize: "0.81rem", // Reduced by 10% (0.9 * 0.9 = 0.81rem)
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px 16px", // More compact padding
          borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
        },
        head: {
          fontWeight: 600,
          backgroundColor: "#f8fafc",
          padding: "12px 16px",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "1px solid #e2e8f0",
          boxShadow: "none",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          borderBottom: "1px solid #e2e8f0",
        },
      },
    },
  },
});

function App() {
  const portalType = import.meta.env.VITE_PORTAL_TYPE || "admin";
  const portalName = import.meta.env.VITE_PORTAL_NAME || "Admin Portal";
  const portalColor = import.meta.env.VITE_PORTAL_COLOR || "#1976d2";

  // Get basename for router - use subfolder path in production
  const basename = import.meta.env.MODE === "development" ? "/" : "/";

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider portalType={portalType}>
          <Router basename={basename}>
            <div className="App">
              <Routes>
                {/* Public routes */}
                <Route
                  path="/login"
                  element={
                    <LoginPage
                      portalType={portalType}
                      portalName={portalName}
                      portalColor={portalColor}
                      redirectPath="/dashboard"
                    />
                  }
                />

                {/* Protected routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute requiredPortal={portalType}>
                      <AdminConsole />
                    </ProtectedRoute>
                  }
                />

                {/* Default redirect */}
                <Route
                  path="/"
                  element={<Navigate to="/dashboard" replace />}
                />
              </Routes>

              {/* Global toast notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: "#363636",
                    color: "#fff",
                  },
                  success: {
                    duration: 3000,
                    theme: {
                      primary: "green",
                      secondary: "black",
                    },
                  },
                }}
              />
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
